/**
 * Cancel-reward dialog for the Rewards activity table.
 *
 * Calls the provider's cancel API (Tango: POST /lineItems/{id}/cancel) via the
 * cancel mutation. The operator must pick a reason code; an "Other" selection
 * reveals a required free-text field. The provider remains the source of truth
 * for whether the reward is still cancellable - a refusal surfaces as a toast.
 *
 * Form driven by generated useForm per the codebase's "hand-rolled forms are
 * banned" rule (CLAUDE.md). The footer Cancel/Confirm row owns layout; the
 * Confirm button submits the form via the HTML `form` attribute.
 */

import { useMemo } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { useForm } from '@/shared/lib/forms'
import type { FormContext, FormField } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'

import { useCancelRewardIssuance } from '@/admin/hooks/use-integrations'
import {
  REWARD_CANCEL_REASONS,
  type RewardCancelReason,
  type RewardIssuanceRow,
} from '@/admin/types/integrations'

const FORM_ID = 'cancel-reward-form'

export interface CancelRewardDialogProps {
  open: boolean
  issuance: RewardIssuanceRow
  onClose: () => void
}

export function CancelRewardDialog({
  open,
  issuance,
  onClose,
}: CancelRewardDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="sm:max-w-md">
        {open && <CancelForm issuance={issuance} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  )
}

function CancelForm({
  issuance,
  onClose,
}: Omit<CancelRewardDialogProps, 'open'>) {
  const cancelMutation = useCancelRewardIssuance()

  const recipient =
    issuance.recipient_name || issuance.recipient_email || 'this recipient'

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'select',
        name: 'reason_code',
        label: 'Reason',
        value: 'DUPLICATE',
        required: 'Choose a reason.',
        options: REWARD_CANCEL_REASONS.map((r) => ({
          value: r.value,
          label: r.label,
        })),
      },
      {
        type: 'textarea',
        name: 'other_reason',
        label: 'Details',
        placeholder: 'Describe why this reward is being cancelled.',
        value: '',
        // Only collected (and required) when the reason is "Other". The
        // cross-field requirement is enforced in the submit handler via
        // setFieldError, since per-field zod can't see sibling values.
        visible: (ctx: FormContext) => ctx.values.reason_code === 'OTHER',
      },
    ],
    [],
  )

  const { Form: ConfirmForm } = useForm(
    {
      name: FORM_ID,
      submit: 'footer',
      fields,
    },
    async (values, formCtx) => {
      const v = values as { reason_code: RewardCancelReason; other_reason?: string }
      const otherReason = (v.other_reason ?? '').trim()
      if (v.reason_code === 'OTHER' && !otherReason) {
        formCtx.setFieldError('other_reason', 'Required when the reason is "Other".')
        return
      }
      try {
        await cancelMutation.mutateAsync({
          uuid: issuance.uuid,
          payload: {
            reason_code: v.reason_code,
            ...(v.reason_code === 'OTHER' ? { other_reason: otherReason } : {}),
          },
        })
        toast.success('Reward cancelled.')
        onClose()
      } catch (err) {
        toastApiError(err, 'Failed to cancel reward.')
      }
    },
    [issuance.uuid],
  )

  return (
    <>
      <DialogHeader>
        <DialogTitle>Cancel reward</DialogTitle>
        <DialogDescription>
          Cancel the reward sent to {recipient}. This requests a cancellation
          with the provider and can only succeed while the reward is unredeemed.
        </DialogDescription>
      </DialogHeader>

      <div className="py-2">
        <ConfirmForm className="flex flex-col gap-4" />
      </div>

      <DialogFooter>
        <Button
          variant="outline"
          onClick={onClose}
          disabled={cancelMutation.isPending}
        >
          Keep reward
        </Button>
        <Button
          type="submit"
          form={FORM_ID}
          variant="destructive"
          disabled={cancelMutation.isPending}
        >
          {cancelMutation.isPending && (
            <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
          )}
          Cancel reward
        </Button>
      </DialogFooter>
    </>
  )
}
